<script setup lang="ts">
	import {msg} from "../../i18n/Message";
	import {ref, computed} from 'vue';
	import ProgressBar from "./ProgressBar.vue";
	import { CrmInstallData, useCrmInstallData } from './CrmInstallData';
	
	import Language from "./step/Language.vue";
	import Selection from "./step/Selection.vue";
	import Upgrade from "./step/Upgrade.vue";
	import Database from "./step/Database.vue";
	import Folders from "./step/Folders.vue";
	import Application from "./step/Application.vue";
	import User from "./step/User.vue";
	import Done from "./step/Done.vue";
	import Install from "./step/Install.vue";
	
	const { getInstallData } = useCrmInstallData();
	
	const props = defineProps({
		lang: { type: String, default: "cs" }
	});
	
	const step = ref("Language");
	const progressBar =ref(null);
	const form = ref<CrmInstallData>({language: props.lang});
	
	const restApi = "../api/install";
	
	const component = computed(() => {
			switch (step.value) {
				case "Language": return Language;
				case "Selection": return Selection;
				case "Upgrade": return Upgrade;
				case "Database": return Database;
				case "Folders": return Folders;
				case "Application": return Application;
				case "User": return User;
				case "Done": return Done;
				case "Install": return Install;
				default: return null;
			}
		});
		
	const upgrade = computed(() => {
			return form.value.selection==='upgrade';
		});
	
	function nextStep(nextStep: string):void {
		step.value=nextStep;
		setProgress(nextStep);
	}
	
	function setProgress(nextStep: string):void {
		switch (nextStep) {
			case "Language": progressBar.value.makeProgress(0);break;
			case "Selection": progressBar.value.makeProgress(10);break;
			case "Upgrade": progressBar.value.makeProgress(15);break;
			case "Database": progressBar.value.makeProgress(20);break;
			case "Folders": progressBar.value.makeProgress(40);break;
			case "Application": progressBar.value.makeProgress(60);break;
			case "User": progressBar.value.makeProgress(80);break;
			case "Done": 
			case "Install": progressBar.value.makeProgress(100);break;
			//default: return null;
		}
	}
	
	function updateForm(property: string, value: any): void {
		//console.log("updating:"+property+" value:"+value);
		form.value[property]=value;
		//console.log("Updated form:"+JSON.stringify(form.value));
	}
	
	function reloadCrm(): void {
		getInstallData(restApi)
			.then((data)=>{
				if (data) {
					data.language = form.value.language;
					form.value=data;
				}
				//console.log("Refreshed form:"+JSON.stringify(form.value));
			});
	}
	
	//onSetup
	reloadCrm();
</script>
<template>
	<div class="installBox">
		<div class="textColumn">	
			<div class="install-comment" style="margin-bottom: 20px;">
				<span >{{msg("install.wizard.progress")}}:&nbsp;</span>
				<progress-bar ref="progressBar" style="display: inline-block;"/>
			</div>
			<template v-if="step=='Language'">
				<div>
				<div class="welc">{{msg("install.wizard.step")}}&nbsp;1:&nbsp;{{msg("install.welcome.title")}}</div>
				<div class="install-comment" v-html="msg('install.welcome.comment')"></div>
				</div>
			</template>
			<template v-else-if="step=='Selection'">
				<div>
				<div class="welc">{{msg("install.wizard.step")}}&nbsp;2:&nbsp;{{msg("install.title.selection")}}</div>
                <div class="install-comment" v-html="msg('install.selection.info')"></div>
				</div>
			</template>
			<template v-else-if="step=='Upgrade'">
				<div>
				<div class="welc">{{msg("install.wizard.step")}}&nbsp;3:&nbsp;{{msg("install.title.oldPath")}}</div>
                <div class="install-comment">{{msg("install.oldPath.info")}} {{msg("install.oldPath.path")}}</div>
				</div>
			</template>
			<template v-else-if="step=='Database'">
				<div>
				<div class="welc">{{msg("install.wizard.step")}}&nbsp;{{upgrade?"4":"3"}}:&nbsp;{{msg("install.title.mysql")}}</div>
                <div class="install-comment" v-html="msg('install.mysql.info')"></div>
                <div class="install-comment">{{msg("install.err.mysql.version")}}</div>
				</div>
			</template>
			<template v-else-if="step=='Folders'">
				<div>
				<div class="welc">{{msg("install.wizard.step")}}&nbsp;{{upgrade?"5":"4"}}:&nbsp;{{msg("install.title.path")}}</div>
                <div class="install-comment" v-html="msg('install.path.info')"></div>
				</div>
			</template>
			<template v-else-if="step=='Application'">
				<div>
				<div class="welc">{{msg("install.wizard.step")}}&nbsp;{{upgrade?"6":"5"}}:&nbsp;{{msg("install.title.app")}}</div>
				<div class="install-comment" v-html="msg('install.app.info')"></div>
				</div>
			</template>
			<template v-else-if="step=='User'">
				<div>
				<div class="welc">{{msg("install.wizard.step")}}&nbsp;{{upgrade?"7":"6"}}:&nbsp;{{msg("install.title.app2")}}</div>
				<div class="install-comment">{{msg("install.user.info")}}</div>
				<div class="install-comment" v-html="msg('install.app2.info')"></div>
				</div>
			</template>
			<template v-else-if="step=='Done'">
				<div>
					<div class="welc">{{msg("install.wizard.step")}}&nbsp;{{upgrade?"8":"7"}}:&nbsp;{{msg(upgrade?"install.title.upgrade":"install.title.install")}}</div>
					<div class="install-comment" v-html="msg(upgrade?'install.done.upgradeText':'install.done.installText')"></div>
				</div>
			</template>
			<template v-else-if="step=='Install'">
				<div>
					<div class="welc">{{msg(upgrade?"install.title.status":"upgrade.title.status")}}</div>
					<div class="install-comment">{{msg('install.done.finishText1')}}</div>
				</div>
			</template>
		</div>
		<div class="separatorColumn"><div class="separator"></div></div>
		<div class="formColumn">
			<Transition name="fade" mode="out-in">
				<component :is="component" v-model:form='form' :rest-api="restApi"
						   @update-form='updateForm' @next-step="nextStep" @reload="reloadCrm"></component>
			</Transition>
		</div>
	</div>
</template>

<style>
	.installBox {
		display: flex;
		flex-direction: row;
		flex-wrap: nowrap;
		justify-content: space-evenly;
		align-items: flex-start;
		background-color: var(--main-bg);
		width: 100%;
		height: 100%;
	}

	.installTable .welc {
		margin-left: 36px;
		font-size: var(--font-size-xxxl);
	}
	
	.installTable .install-comment {
		margin: 10px 36px 10px 36px;
		font-size: var(--font-size-xl);
	}

	.installTable .textColumn,
	.installTable .formColumn {
		padding-top: 20px;
		width: 48%;
	}
	
	.installTable .separatorColumn {
		width: 4%;
		text-align: center;
	}

	div.separator {
		margin-left: 1px;
		width: 1px;
		height: 450px;
		border-left: 1px solid var(--h10);
	}

	.formTitle {
		margin-bottom: 20px;
		font-size: var(--font-size-xxl);
	}

	.formTable {
		margin-left: 20px;
		color: var(--fg);
	}
	
	.formTable th {
		text-align: left;
		margin-left: 0;
		font-size: var(--font-size-l);
		padding: 2px;
	}

	.formTable .button  {
		font-size: var(--font-size-xl);
	}

	.fade-enter-active {
		transition: opacity 0.1s ease;
	}

	.fade-leave-active {
		transition: opacity 0.3s ease;
	}

	.fade-enter-from, 
	.fade-leave-to {
		opacity: 0;
	}

	.errorfade-enter-active,
	.errorfade-leave-active {
		transition: opacity 1.0s ease;
	}

	.errorfade-enter-from,
	.errorfade-leave-to {
		opacity: 0;
	}

	.install-comment {
		font-size: var(--font-size-s);
	}
</style>
